/* ============================================
   EVENT RESOLVER - Arbitrage des événements audio
   ============================================
   Couche d'arbitrage entre le feed MQTT et le moteur audio.
   Décide si un événement mérite une réaction sonore,
   en appliquant déduplication, cooldowns, throttling
   et résolution d'états Layer 2.
   ============================================ */

import {
  EVENT_SOUND_MAP,
  LAYER2_STATES,
  EVENT_TO_LAYER2_STATE,
  LAYER2_RESOLUTION_EVENTS,
  SOUND_COOLDOWNS,
  SOUND_STACKS,
} from './sound-mapping.js'

export class EventResolver {
  constructor() {
    // Déduplication temporelle
    this.lastEventType = null
    this.lastEventTime = 0

    // Cooldowns par famille de sons
    this.lastPlayedAt = {}  // soundKey -> timestamp

    // Throttling global
    this.recentOneShots = []     // timestamps des one-shots récents
    this.lastLayer2Transition = 0 // timestamp de la dernière transition Layer 2

    // État Layer 2 courant
    this.currentLayer2State = 'idle'
  }

  /**
   * Décider quoi faire avec un événement.
   *
   * @param {string} eventType — type d'événement brut du feed MQTT
   * @param {object} context   — état courant (optionnel, pour extensions futures)
   * @returns {object} decision
   *   action: 'play_oneshot' | 'play_stack' | 'transition_layer2' | 'resolve_layer2' | 'ignore'
   *   soundKey: string | null       — clé du son à jouer (pour play_oneshot)
   *   stack: array | null           — séquence de sons (pour play_stack)
   *   layer2State: string | null    — nouvel état Layer 2 (pour transition_layer2)
   *   reason: string                — raison de la décision (pour debug)
   */
  resolve(eventType, context = {}) {
    const now = Date.now()
    const mapping = EVENT_SOUND_MAP[eventType]

    // --- Déduplication temporelle ---
    if (eventType === this.lastEventType) {
      const elapsed = now - this.lastEventTime
      if (elapsed < 2000) {
        return { action: 'ignore', reason: `dedup: même événement < 2s (${elapsed}ms)` }
      }
    }

    // Événement mineur < 500ms après un événement majeur → ignore
    if (this.lastEventTime && (now - this.lastEventTime) < 500) {
      const lastMapping = EVENT_SOUND_MAP[this.lastEventType]
      if (lastMapping && mapping) {
        const lastIsStack = !!SOUND_STACKS[this.lastEventType]
        if (lastIsStack && !SOUND_STACKS[eventType]) {
          return { action: 'ignore', reason: 'hiérarchie: événement mineur trop proche d\'un majeur' }
        }
      }
    }

    // Enregistrer cet événement
    this.lastEventType = eventType
    this.lastEventTime = now

    // --- Résolution Layer 2 ---
    const layer2Target = EVENT_TO_LAYER2_STATE[eventType]
    const isResolutionEvent = LAYER2_RESOLUTION_EVENTS.includes(eventType)
    let layer2Resolved = false

    // Si l'événement résout un état Layer 2 actif
    if (isResolutionEvent && this.currentLayer2State !== 'idle') {
      const currentState = LAYER2_STATES[this.currentLayer2State]
      if (currentState) {
        this.currentLayer2State = 'idle'
        layer2Resolved = true
      }
    }

    // Si l'événement déclenche un nouvel état Layer 2
    let layer2Decision = null
    if (layer2Target) {
      layer2Decision = this._resolveLayer2Transition(layer2Target, now)
    } else if (layer2Resolved) {
      // L'événement a résolu l'état Layer 2 → transition vers idle
      layer2Decision = {
        action: 'transition_layer2',
        layer2State: 'idle',
        soundKey: null,
        stack: null,
        reason: `resolve_layer2: retour idle via ${eventType}`,
      }
    }

    // --- Sound stacks ---
    const stack = SOUND_STACKS[eventType]
    if (stack) {
      // Vérifier le cooldown du son principal du stack
      const primaryKey = stack[0]?.key
      if (primaryKey && this._isOnCooldown(primaryKey, now)) {
        return layer2Decision || { action: 'ignore', reason: `cooldown: ${primaryKey} encore en cooldown` }
      }

      // Vérifier le throttling global
      if (this._isThrottled(now)) {
        return layer2Decision || { action: 'ignore', reason: 'throttle: trop de one-shots récents' }
      }

      this._recordOneShot(now)
      if (primaryKey) this.lastPlayedAt[primaryKey] = now

      return {
        action: 'play_stack',
        stack,
        soundKey: null,
        layer2State: layer2Decision?.layer2State || null,
        reason: `stack: ${eventType}` + (layer2Decision ? ` + layer2: ${layer2Decision.layer2State}` : ''),
      }
    }

    // --- One-shot simple ---
    if (mapping && mapping.layer === 3) {
      const soundKey = mapping.key

      // Vérifier le cooldown
      if (this._isOnCooldown(soundKey, now)) {
        return layer2Decision || { action: 'ignore', reason: `cooldown: ${soundKey} encore en cooldown` }
      }

      // Vérifier le throttling global
      if (this._isThrottled(now)) {
        return layer2Decision || { action: 'ignore', reason: 'throttle: trop de one-shots récents' }
      }

      this._recordOneShot(now)
      this.lastPlayedAt[soundKey] = now

      return {
        action: 'play_oneshot',
        soundKey,
        stack: null,
        layer2State: layer2Decision?.layer2State || null,
        reason: `oneshot: ${soundKey}` + (layer2Decision ? ` + layer2: ${layer2Decision.layer2State}` : ''),
      }
    }

    // Événement Layer 2 seul (pas de one-shot associé)
    if (layer2Decision) return layer2Decision

    // Événement non mappé
    return { action: 'ignore', reason: `non mappé: ${eventType}` }
  }

  /**
   * Résoudre une transition Layer 2.
   */
  _resolveLayer2Transition(targetStateName, now) {
    const targetState = LAYER2_STATES[targetStateName]
    const currentState = LAYER2_STATES[this.currentLayer2State]

    if (!targetState) return null

    // Même état → ignore
    if (targetStateName === this.currentLayer2State) return null

    // Priorité insuffisante
    if (targetState.priority < currentState.priority) {
      return {
        action: 'ignore',
        reason: `layer2: ${targetStateName} (prio ${targetState.priority}) < ${this.currentLayer2State} (prio ${currentState.priority})`,
      }
    }

    // Throttling : max 1 transition Layer 2 par 10s
    if (now - this.lastLayer2Transition < 10000 && targetState.priority <= currentState.priority) {
      return {
        action: 'ignore',
        reason: `throttle: transition Layer 2 < 10s`,
      }
    }

    this.lastLayer2Transition = now
    this.currentLayer2State = targetStateName

    return {
      action: 'transition_layer2',
      layer2State: targetStateName,
      soundKey: null,
      stack: null,
      reason: `layer2: ${targetStateName}`,
    }
  }

  /**
   * Vérifier si un son est encore en cooldown.
   */
  _isOnCooldown(soundKey, now) {
    const cooldownMs = SOUND_COOLDOWNS[soundKey]
    if (!cooldownMs) return false

    const lastPlayed = this.lastPlayedAt[soundKey] || 0
    return (now - lastPlayed) < cooldownMs
  }

  /**
   * Vérifier le throttling global (max 2 one-shots en 3 secondes).
   */
  _isThrottled(now) {
    // Nettoyer les entrées > 3s
    this.recentOneShots = this.recentOneShots.filter(t => (now - t) < 3000)
    return this.recentOneShots.length >= 2
  }

  /**
   * Enregistrer un one-shot pour le throttling.
   */
  _recordOneShot(now) {
    this.recentOneShots.push(now)
  }

  /**
   * Forcer le retour à l'état idle (appelé par le moteur en fin de match, etc.).
   */
  resetLayer2() {
    this.currentLayer2State = 'idle'
  }

  /**
   * Reset complet (nouveau match).
   */
  reset() {
    this.lastEventType = null
    this.lastEventTime = 0
    this.lastPlayedAt = {}
    this.recentOneShots = []
    this.lastLayer2Transition = 0
    this.currentLayer2State = 'idle'
  }
}
